/**
 * Composant - WithdrawalFormStep
 *
 * Étape 4: Formulaire de retrait (montant et destination)
 *
 * Améliorations:
 * - Boutons de montants rapides (QuickAmountSelector)
 * - Validation du solde en temps réel
 * - Avertissement pour les montants élevés
 * - Affichage du solde disponible et restant
 */

import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Lock,
  RotateCcw,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WithdrawalState } from '../../../domain/aggregates/WithdrawalWorkflow';
import { createEmptyDenominationMap, calculateTotal } from '../../../shared/constants/denominations';
import { montantEnLettres } from '../../../shared/utils/montantEnLettres';
import { HighAmountWarning } from './shared/HighAmountWarning';
import { SelecteurCoupures } from '../common/SelecteurCoupures';
import { QUICK_AMOUNTS, formatAmount, WITHDRAWAL_THRESHOLDS } from './types';
import { useAuth } from '../../contexts/AuthContext';
import './etapes.css';

interface WithdrawalFormStepProps {
  workflow: any;
  currentBalance?: number;
  dailyLimit?: number;
  dailyUsed?: number;
}

export function WithdrawalFormStep({
  workflow,
  currentBalance = 0,
  dailyLimit = WITHDRAWAL_THRESHOLDS.dailyLimit,
  dailyUsed = 0
}: WithdrawalFormStepProps) {
  const { state, context, setWithdrawalDetails, submitWithdrawal, loading } = workflow;
  const { user } = useAuth();

  // Récupérer le dernier client authentifié
  const authenticatedClient = context.authenticatedClients?.[0];
  const accountHolder = context?.account?.primaryHolder;


  // Utiliser le solde du contexte si disponible
  const balance = currentBalance || context?.account?.balance?.amount || 0;

  const [bills, setBills] = useState<Map<number, number>>(createEmptyDenominationMap());
  const [motifRetrait, setMotifRetrait] = useState('');
  const [motifPersonnalise, setMotifPersonnalise] = useState('');
  const [notes, setNotes] = useState('');
  const [showHighAmountWarning, setShowHighAmountWarning] = useState(false);

  const total = calculateTotal(bills);

  // Calculs de validation
  const remainingDaily = Math.max(0, dailyLimit - dailyUsed);
  const maxWithdrawable = Math.min(balance, remainingDaily, WITHDRAWAL_THRESHOLDS.transactionLimit);
  const isOverBalance = total > balance;
  const isOverDailyLimit = total > remainingDaily;
  const isOverTransactionLimit = total > WITHDRAWAL_THRESHOLDS.transactionLimit;
  const isHighAmount = total >= WITHDRAWAL_THRESHOLDS.highAmountWarning;
  const requiresSupervisor = total >= WITHDRAWAL_THRESHOLDS.supervisorRequired;

  // Validation globale
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (total <= 0) errors.push('Veuillez saisir un montant');
    if (!motifRetrait) errors.push('Veuillez sélectionner un motif');
    if (motifRetrait === 'Autre' && !motifPersonnalise.trim()) errors.push('Veuillez préciser le motif personnalisé');
    if (isOverBalance) errors.push(`Solde insuffisant (max: ${formatAmount(balance)})`);
    if (isOverDailyLimit) errors.push(`Dépasse le plafond journalier restant (${formatAmount(remainingDaily)})`);
    if (isOverTransactionLimit) errors.push(`Dépasse le maximum par transaction (${formatAmount(WITHDRAWAL_THRESHOLDS.transactionLimit)})`);
    return errors;
  }, [total, motifRetrait, motifPersonnalise, isOverBalance, isOverDailyLimit, isOverTransactionLimit, balance, remainingDaily]);

  const isValid = validationErrors.length === 0 && total > 0 && motifRetrait && (motifRetrait !== 'Autre' || motifPersonnalise.trim().length > 0);

  const handleBillChange = (denomination: number, count: number) => {
    const newBills = new Map(bills);
    newBills.set(denomination, count);
    setBills(newBills);
  };

  /** Boutons − / + : mise à jour fonctionnelle, aucun clic perdu même très rapide */
  const handleBillDelta = (denomination: number, delta: number) => {
    setBills((prev) => {
      const next = new Map(prev);
      next.set(denomination, Math.max(0, (prev.get(denomination) ?? 0) + delta));
      return next;
    });
  };

  // Gestion des montants rapides
  const handleQuickAmountSelect = (amount: number) => {
    // Répartir le montant en billets (privilégier les grosses coupures)
    const newBills = createEmptyDenominationMap();
    let remaining = amount;

    // Trier les dénominations par ordre décroissant
    const sortedDenoms = [10000, 5000, 2000, 1000, 500];

    for (const denom of sortedDenoms) {
      if (remaining >= denom) {
        const count = Math.floor(remaining / denom);
        newBills.set(denom, count);
        remaining -= count * denom;
      }
    }

    setBills(newBills);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid) {
      return;
    }

    // Si montant élevé, afficher l'avertissement
    if (isHighAmount && !showHighAmountWarning) {
      setShowHighAmountWarning(true);
      return;
    }

    await executeWithdrawal();
  };

  const executeWithdrawal = async () => {
    // Utiliser le motif sélectionné ou personnalisé
    const destinationInfo = motifRetrait === 'Autre' ? motifPersonnalise.trim() : motifRetrait;

    // Construire les notes avec les infos de destination
    let withdrawalNotes = `Retrait personnel - ${accountHolder?.name || 'Client'}`;

    // Ajouter les notes optionnelles
    if (notes.trim()) {
      withdrawalNotes += `. ${notes}`;
    }

    // Définir les détails du retrait
    setWithdrawalDetails(total, bills, destinationInfo, withdrawalNotes || undefined);

    // Soumettre le retrait avec les infos du caissier connecté
    const agentCode = user?.cardData?.code || user?.username || '1080W';
    const agentName = user?.displayName || 'Agent Principal';
    await submitWithdrawal(agentCode, agentName);
  };

  const handleHighAmountConfirm = () => {
    setShowHighAmountWarning(false);
    executeWithdrawal();
  };

  const handleHighAmountModify = () => {
    setShowHighAmountWarning(false);
  };

  if (!authenticatedClient && !accountHolder) {
    return (
      <div className="et">
        <div className="et-note erreur">
          <AlertTriangle />
          <p>Aucun client authentifié : recommencez le retrait depuis l&apos;étape Biométrie.</p>
        </div>
      </div>
    );
  }

  const isValidating = state === WithdrawalState.VALIDATION_PENDING;
  const clientName = authenticatedClient?.clientName || accountHolder?.name || 'Client';
  const numeroCompte = authenticatedClient?.accountNumber || context?.account?.accountNumber?.value;
  const nbSignataires = context.authenticatedClients?.length ?? 1;
  // Seules les erreurs liées au montant sont affichées en rouge : le motif est un champ requis
  // signalé par le bouton désactivé, inutile d'alerter avant que le caissier y arrive.
  const erreursMontant = validationErrors.filter((e) => !e.startsWith('Veuillez'));

  return (
    <>
      {/* Modal avertissement montant élevé */}
      <HighAmountWarning
        amount={total}
        onConfirm={handleHighAmountConfirm}
        onModify={handleHighAmountModify}
        isVisible={showHighAmountWarning}
      />

      <div className="et et-large">
        <section className="et-carte">
          <header className="et-entete">
            <span className="ico">
              <Wallet />
            </span>
            <div>
              <h2>Montant du retrait</h2>
              <p>Saisissez les coupures à remettre au client.</p>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="et-corps">
            {/* Client */}
            <div className="et-client">
              <span className="et-avatar">{initiales(clientName)}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p className="nom">{clientName}</p>
                <p className="sous">
                  {numeroCompte ? (
                    <>
                      Compte <span className="et-mono">{numeroCompte}</span>
                    </>
                  ) : (
                    'Compte déterminé par la carte'
                  )}
                </p>
              </div>
              <span className="et-pastille vert">
                <ShieldCheck /> {nbSignataires > 1 ? `${nbSignataires} signataires vérifiés` : 'Identité vérifiée'}
              </span>
            </div>

            {/* Montant total */}
            <div className="et-montant" aria-live="polite">
              <p className="lib">Montant à remettre</p>
              <p className={`val ${total > 0 ? '' : 'vide'}`}>{formatAmount(total)}</p>
              <p className="lettres">{total > 0 ? montantEnLettres(total) : 'Choisissez un montant ou des coupures'}</p>
            </div>

            {/* Montants rapides */}
            <div>
              <p className="et-section-titre">
                <span>Montants rapides</span>
              </p>
              <div className="et-rapides">
                {QUICK_AMOUNTS.map((montant) => (
                  <button
                    key={montant}
                    type="button"
                    className={`et-rapide ${total === montant ? 'actif' : ''}`}
                    disabled={montant > maxWithdrawable}
                    onClick={() => handleQuickAmountSelect(montant)}
                  >
                    {formatAmount(montant)}
                  </button>
                ))}
              </div>
            </div>

            {/* Coupures */}
            <div>
              <p className="et-section-titre">
                <span>
                  <Banknote /> Coupures
                </span>
                {total > 0 && (
                  <button type="button" className="et-btn lien" onClick={() => setBills(createEmptyDenominationMap())}>
                    <RotateCcw /> Remettre à zéro
                  </button>
                )}
              </p>
              <SelecteurCoupures coupures={bills} onChange={handleBillChange} onDelta={handleBillDelta} />
            </div>

            {/* Erreurs de validation */}
            <AnimatePresence>
              {erreursMontant.length > 0 && total > 0 && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                  <div className="et-note erreur">
                    <AlertTriangle />
                    <div>
                      <strong>Impossible de continuer</strong>
                      <ul>
                        {erreursMontant.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Avertissement montant élevé */}
            {isHighAmount && isValid && (
              <div className="et-note alerte">
                <AlertTriangle />
                <p>
                  <strong>Montant élevé</strong>
                  Ce montant dépasse {formatAmount(WITHDRAWAL_THRESHOLDS.highAmountWarning)}.
                  {requiresSupervisor && ' Une validation superviseur sera requise.'}
                </p>
              </div>
            )}

            {/* Motif */}
            <div className="et-grille">
              <div className={`et-champ ${motifRetrait === 'Autre' ? '' : 'pleine'}`}>
                <label htmlFor="motif">
                  Motif du retrait <span className="req">*</span>
                </label>
                <select
                  id="motif"
                  className="et-input"
                  value={motifRetrait}
                  onChange={(e) => {
                    setMotifRetrait(e.target.value);
                    if (e.target.value !== 'Autre') {
                      setMotifPersonnalise('');
                    }
                  }}
                  required
                >
                  <option value="">Sélectionnez un motif</option>
                  {MOTIFS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                  <option value="Autre">Autre (à préciser)</option>
                </select>
              </div>
              {motifRetrait === 'Autre' && (
                <div className="et-champ">
                  <label htmlFor="motif-autre">
                    Précisez le motif <span className="req">*</span>
                  </label>
                  <input
                    id="motif-autre"
                    className="et-input"
                    type="text"
                    value={motifPersonnalise}
                    onChange={(e) => setMotifPersonnalise(e.target.value)}
                    placeholder="Motif du retrait"
                    required
                  />
                </div>
              )}
              <div className="et-champ pleine">
                <label htmlFor="notes">
                  Notes <span className="opt">(facultatif)</span>
                </label>
                <textarea
                  id="notes"
                  className="et-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Informations complémentaires"
                  rows={2}
                />
              </div>
            </div>

            <button type="submit" disabled={loading || isValidating || !isValid} className="et-btn principal">
              {isValidating ? (
                <>
                  <RotateCcw className="et-tourne" /> Traitement en cours…
                </>
              ) : total > 0 ? (
                <>
                  Valider le retrait de {formatAmount(total)}
                  <ArrowRight />
                </>
              ) : (
                <>
                  Valider le retrait
                  <ArrowRight />
                </>
              )}
            </button>
          </form>

          <p className="et-pied">
            <Lock />
            Le retrait est débité au CBS dès la validation et ne peut plus être annulé.
          </p>
        </section>
      </div>
    </>
  );
}

const MOTIFS = [
  'Retrait personnel',
  'Achat de matériel',
  'Frais médicaux',
  'Frais de scolarité',
  'Paiement de loyer',
  'Frais de voyage',
  'Dépenses quotidiennes',
  'Paiement de factures',
  'Aide familiale',
  'Investissement',
  'Commerce',
  'Agriculture',
];

function initiales(nom?: string) {
  return (
    (nom ?? '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0]?.toUpperCase())
      .join('') || '?'
  );
}
